import Konva from 'konva';
import { BaseShape } from '../core/shapes/BaseShape';

export interface ShapeInfo {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  absolutePosition: { x: number; y: number };
  isVisible: boolean;
  isSelected: boolean;
}

export interface TestMouseOptions {
  ctrlKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  button?: number;
}

export interface GuideCounts {
  alignment: number;
  distribution: number;
}

export class CanvasTestingBridge {
  private stage: Konva.Stage;
  private selectedIds: Set<string>;
  private shapeMap: Map<string, BaseShape<any>>;
  private viewportCommitter: ((next: { scale: number; offsetX: number; offsetY: number }) => void) | null = null;

  constructor(stage: Konva.Stage, selectedIds?: Set<string>) {
    this.stage = stage;
    this.selectedIds = selectedIds || new Set();
    this.shapeMap = new Map();
    
    if (typeof window !== 'undefined') {
      (window as any).__canvasTestBridge = this;
    }
  }

  // -------- Table Shape helpers (command-driven via TestHarness) --------

  async createTable(params: { x: number; y: number; schemaId?: string }): Promise<string> {
    const h = (window as any).TestHarness;
    if (!h?.createTable) {
      throw new Error('TestHarness.createTable not available');
    }
    const id = await h.createTable({ x: params.x, y: params.y, schemaId: params?.schemaId ?? 'blank' });
    if (!id || typeof id !== 'string') throw new Error('Failed to create table');
    return id;
  }

  async selectTableCell(tableId: string, row: number, col: number, opts?: { extend?: boolean }): Promise<void> {
    const h = (window as any).TestHarness;
    if (!h?.selectTableCell) {
      throw new Error('TestHarness.selectTableCell not available');
    }
    await h.selectTableCell(tableId, { row, col, extend: !!opts?.extend });
    await this.wait(10);
  }

  async reorderTableRow(tableId: string, fromIndex: number, toIndex: number): Promise<void> {
    const h = (window as any).TestHarness;
    if (!h?.reorderTableRow) {
      throw new Error('TestHarness.reorderTableRow not available');
    }
    await h.reorderTableRow(tableId, { fromIndex, toIndex });
    await this.wait(10);
  }

  async reorderTableColumn(tableId: string, fromIndex: number, toIndex: number): Promise<void> {
    const h = (window as any).TestHarness;
    if (!h?.reorderTableColumn) {
      throw new Error('TestHarness.reorderTableColumn not available');
    }
    await h.reorderTableColumn(tableId, { fromIndex, toIndex });
    await this.wait(10);
  }

  async resizeTableRow(tableId: string, index: number, delta: number): Promise<void> {
    const h = (window as any).TestHarness;
    if (!h?.resizeTableRow) {
      throw new Error('TestHarness.resizeTableRow not available');
    }
    await h.resizeTableRow(tableId, { index, delta });
    await this.wait(10);
  }

  async resizeTableColumn(tableId: string, index: number, delta: number): Promise<void> {
    const h = (window as any).TestHarness;
    if (!h?.resizeTableColumn) {
      throw new Error('TestHarness.resizeTableColumn not available');
    }
    await h.resizeTableColumn(tableId, { index, delta });
    await this.wait(10);
  }

  async scaleTableByDelta(tableId: string, deltaWidth: number, deltaHeight: number): Promise<void> {
    const h = (window as any).TestHarness;
    if (!h?.scaleTableByDelta) {
      throw new Error('TestHarness.scaleTableByDelta not available');
    }
    await h.scaleTableByDelta(tableId, { deltaWidth, deltaHeight });
    await this.wait(10);
  }

  async setTableCellText(tableId: string, row: number, col: number, text: string): Promise<void> {
    const h = (window as any).TestHarness;
    if (!h?.setTableCellText) {
      throw new Error('TestHarness.setTableCellText not available');
    }
    await h.setTableCellText(tableId, { row, col, text });
    await this.wait(10);
  }

  getNodeById<T = any>(id: string): T | null {
    const h = (window as any).TestHarness;
    if (!h?.getNodeById) return null;
    return h.getNodeById(id) as T | null;
  }

  selectTableCellRange(tableId: string, startRow: number, startCol: number, endRow: number, endCol: number): boolean {
    const h = (window as any).TestHarness;
    if (!h?.selectTableCellRange) return false;
    try { return !!h.selectTableCellRange(tableId, { startRow, startCol, endRow, endCol }); } catch { return false; }
  }

  getTableCellSelection(tableId: string): { anchorKey?: string; selectedKeys: string[] } {
    const h = (window as any).TestHarness;
    if (h?.getTableCellSelection) {
      try { return h.getTableCellSelection(tableId) as { anchorKey?: string; selectedKeys: string[] }; } catch {}
    }
    return { anchorKey: undefined, selectedKeys: [] };
  }

  isTableCellEditing(tableId: string, row: number, col: number): boolean {
    const h = (window as any).TestHarness;
    if (h?.isTableCellEditing) {
      try { return !!h.isTableCellEditing(tableId, { row, col }); } catch {}
    }
    return false;
  }

  async insertTableRow(tableId: string, index: number, direction: 'before' | 'after'): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.insertTableRow?.(tableId, { index, direction });
  }
  async removeTableRow(tableId: string, index: number): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.removeTableRow?.(tableId, { index });
  }
  async insertTableColumn(tableId: string, index: number, direction: 'before' | 'after'): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.insertTableColumn?.(tableId, { index, direction });
  }
  async removeTableColumn(tableId: string, index: number): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.removeTableColumn?.(tableId, { index });
  }
  async updateTableGrid(tableId: string, patch: Partial<{ rowCount: number; columnCount: number; showHeader: boolean; showHeaderColumn: boolean; autoResizeCells: boolean }>): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.updateTableGrid?.(tableId, patch);
  }

  async applyCellStyle(tableId: string, cells: { row: number; col: number } | Array<{ row: number; col: number }>, patch: Record<string, unknown>): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.applyCellStyle?.(tableId, cells, patch);
  }
  async clearCellStyles(tableId: string, cells: { row: number; col: number } | Array<{ row: number; col: number }>): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.clearCellStyles?.(tableId, cells);
  }
  getCellStyle(tableId: string, cell: { row: number; col: number }): any {
    const h = (window as any).TestHarness;
    return h?.getCellStyle?.(tableId, cell);
  }

  async setCellLayout(tableId: string, cell: { row: number; col: number } | Array<{ row: number; col: number }>, layout: { algorithmId: 'fixed' | 'grid'; params?: Record<string, unknown> }): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.setCellLayout?.(tableId, cell, layout);
  }
  async mergeCells(tableId: string, region: { startRow: number; startCol: number; endRow: number; endCol: number }): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.mergeCells?.(tableId, region);
  }
  
  async mergeTableCells(tableId: string, region: { startRow: number; startCol: number; endRow: number; endCol: number }): Promise<void> {
    const h = (window as any).TestHarness;
    // Preserve the historical fallback expression: if mergeTableCells resolves nullish, try mergeCells.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    await h?.mergeTableCells?.(tableId, region) ?? await h?.mergeCells?.(tableId, region);
  }
  async unmergeCells(tableId: string, cell: { row: number; col: number }): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.unmergeCells?.(tableId, cell);
  }

  async copyTableCellSelection(): Promise<boolean> {
    const h = (window as any).TestHarness;
    return !!(await h?.copyTableCellSelection?.());
  }

  async pasteTableCellSelection(tableId: string, cell: { row: number; col: number }): Promise<boolean> {
    const h = (window as any).TestHarness;
    return !!(await h?.pasteTableCellSelection?.(tableId, cell));
  }

  async dockShapeToCell(shapeId: string, tableId: string, cell: { row: number; col: number }, offset?: { x?: number; y?: number }): Promise<void> {
    const h = (window as any).TestHarness;
    await h?.dockShapeToCell?.(shapeId, tableId, { ...cell, offset });
  }

  getDockedNodesInCell(tableId: string, cell: { row: number; col: number }): any[] {
    const h = (window as any).TestHarness;
    return h?.getDockedNodesInCell?.(tableId, cell) ?? [];
  }
  
  getDockedNodesInTable(tableId: string): any[] {
    const h = (window as any).TestHarness;
    return h?.getDockedNodes?.(tableId) ?? [];
  }
  
  updateShapeMap(shapes: BaseShape<any>[]) {
    this.shapeMap.clear();
    shapes.forEach(shape => {
      this.shapeMap.set(shape.id, shape);
    });
  }
  
  updateSelectedIds(selectedIds: Set<string>) {
    this.selectedIds = selectedIds;
  }
  
  getShapeById(id: string): ShapeInfo | null {
    const node = this.stage.findOne(`#${id}`);
    if (!node) return null;
    
    const clientRect = node.getClientRect();
    
    // Try to get position from TestHarness (store) for most accurate data
    let x = node.x();
    let y = node.y();
    const harness = (window as any).TestHarness;
    if (harness?.getNodePosition) {
      const pos = harness.getNodePosition(id);
      if (pos) {
        x = pos.x;
        y = pos.y;
      }
    }
    
    return {
      id: node.id(),
      type: node.className,
      x,
      y,
      width: clientRect.width,
      height: clientRect.height,
      absolutePosition: node.getAbsolutePosition(),
      isVisible: node.visible(),
      isSelected: this.selectedIds.has(id)
    };
  }
  
  getAllShapes(): ShapeInfo[] {
    const shapes: ShapeInfo[] = [];
    
    this.stage.find('.shape').forEach(node => {
      const info = this.getShapeById(node.id());
      if (info) shapes.push(info);
    });
    
    this.stage.find('Group').forEach(node => {
      if (node.id() && !shapes.find(s => s.id === node.id())) {
        const info = this.getShapeById(node.id());
        if (info) shapes.push(info);
      }
    });
    
    return shapes;
  }
  
  getSelectedShapes(): string[] {
    return Array.from(this.selectedIds);
  }

  getGuideCounts(): GuideCounts {
    const alignment = this.stage.find('.alignment-guide')?.length ?? 0;
    const distribution = this.stage.find('.distribution-guide')?.length ?? 0;
    return { alignment, distribution };
  }
  
  getCanvasBounds(): DOMRect {
    const container = this.stage.container();
    return container.getBoundingClientRect();
  }
  
  getStagePosition(): { x: number; y: number; scale: number } {
    return { x: this.stage.x(), y: this.stage.y(), scale: this.stage.scaleX() };
  }
  
  private isKnownDiagramItem(id: string): boolean {
    const harness = (window as any).TestHarness;
    if (!harness) return false;

    const inNodes = (harness.getNodes?.() ?? []).some((node: any) => node?.id === id);
    if (inNodes) return true;

    return (harness.getEdges?.() ?? []).some((edge: any) => edge?.id === id);
  }

  async clickShape(id: string, options: TestMouseOptions = {}): Promise<void> {
    const harness = (window as any).TestHarness;

    // Connectors are registered via refs, not Konva ids; select through TestHarness when present.
    if (harness?.selectById && this.isKnownDiagramItem(id)) {
      if (options.shiftKey || options.ctrlKey || options.metaKey) {
        const current = harness.getSelection?.() || [];
        harness.selectMany?.([...current, id]);
      } else {
        harness.selectById(id);
      }
      await this.wait(20);
      return;
    }

    const shape = this.stage.findOne(`#${id}`);
    if (!shape) throw new Error(`Shape ${id} not found`);
    
    // Fallback: fire Konva events directly
    const rect: any = (shape as any).getClientRect?.() || { x: (shape as any).x(), y: (shape as any).y(), width: (shape as any).width?.() || 1, height: (shape as any).height?.() || 1 };
    const centerCanvas = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
    const stagePos = this.canvasToClient(centerCanvas);
    
    (this.stage as any).setPointersPositions({ clientX: stagePos.x, clientY: stagePos.y });
    
    const mockEvt = new MouseEvent('click', {
      clientX: stagePos.x,
      clientY: stagePos.y,
      bubbles: true,
      cancelable: true,
      view: window,
      ctrlKey: options.ctrlKey ?? false,
      shiftKey: options.shiftKey ?? false,
      metaKey: options.metaKey ?? false,
      altKey: options.altKey ?? false,
      button: options.button ?? 0
    });
    
    shape.fire('mousedown', { evt: mockEvt, target: shape, currentTarget: shape });
    await this.wait(10);
    shape.fire('mouseup', { evt: mockEvt, target: shape, currentTarget: shape });
    await this.wait(10);
    shape.fire('click', { evt: mockEvt, target: shape, currentTarget: shape });
    await this.wait(10);
  }
  
  async dragShape(id: string, deltaX: number, deltaY: number, options: TestMouseOptions = {}): Promise<void> {
    const shape = this.stage.findOne(`#${id}`);
    if (!shape) throw new Error(`Shape ${id} not found`);
    
    // First select the shape via click
    await this.clickShape(id, options);
    await this.wait(50);
    
    // Use TestHarness.moveShape which executes via the command system
    // This tests the actual business logic path including undo/redo
    const harness = (window as any).TestHarness;
    if (harness?.moveShape) {
      await harness.moveShape(id, deltaX, deltaY);
      await this.wait(50);
      return;
    }
    
    // Fallback: Fire Konva drag events (may not work with React-Konva)
    const startX = shape.x();
    const startY = shape.y();
    const newX = startX + deltaX;
    const newY = startY + deltaY;
    
    const rect: any = (shape as any).getClientRect?.() || { x: startX, y: startY, width: (shape as any).width?.() || 1, height: (shape as any).height?.() || 1 };
    const startCanvas = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
    const endCanvas = { x: startCanvas.x + deltaX, y: startCanvas.y + deltaY };
    const startStagePos = this.canvasToClient(startCanvas);
    const endStagePos = this.canvasToClient(endCanvas);
    
    const startEvt = new MouseEvent('mousedown', {
      clientX: startStagePos.x,
      clientY: startStagePos.y,
      bubbles: true,
      cancelable: true,
      view: window,
      button: 0
    });
    
    (this.stage as any).setPointersPositions({ clientX: startStagePos.x, clientY: startStagePos.y });
    shape.fire('mousedown', { evt: startEvt, target: shape, currentTarget: shape });
    shape.fire('dragstart', { evt: startEvt, target: shape, currentTarget: shape });
    await this.wait(20);
    
    shape.position({ x: newX, y: newY });
    
    const endEvt = new MouseEvent('mouseup', {
      clientX: endStagePos.x,
      clientY: endStagePos.y,
      bubbles: true,
      cancelable: true,
      view: window,
      button: 0
    });
    (this.stage as any).setPointersPositions({ clientX: endStagePos.x, clientY: endStagePos.y });
    shape.fire('dragend', { evt: endEvt, target: shape, currentTarget: shape });
    shape.fire('mouseup', { evt: endEvt, target: shape, currentTarget: shape });
    
    this.stage.batchDraw();
    await this.wait(20);
  }

  async dragShapeKonva(
    id: string,
    deltaX: number,
    deltaY: number,
    options: TestMouseOptions & { steps?: number; sampleGuides?: boolean } = {},
  ): Promise<{ maxAlignment: number; maxDistribution: number }> {
    const shape = this.stage.findOne(`#${id}`);
    if (!shape) throw new Error(`Shape ${id} not found`);

    await this.clickShape(id, options);
    await this.wait(50);

    const steps = Math.max(1, options.steps ?? 12);
    const sampleGuides = options.sampleGuides ?? true;

    const startX = shape.x();
    const startY = shape.y();
    const endX = startX + deltaX;
    const endY = startY + deltaY;

    const rect: any =
      (shape as any).getClientRect?.() || { x: startX, y: startY, width: (shape as any).width?.() || 1, height: (shape as any).height?.() || 1 };
    const startCanvas = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
    const endCanvas = { x: startCanvas.x + deltaX, y: startCanvas.y + deltaY };
    const startStagePos = this.canvasToClient(startCanvas);
    const endStagePos = this.canvasToClient(endCanvas);

    let maxAlignment = 0;
    let maxDistribution = 0;

    const startEvt = new MouseEvent('mousedown', {
      clientX: startStagePos.x,
      clientY: startStagePos.y,
      bubbles: true,
      cancelable: true,
      view: window,
      button: 0,
      ctrlKey: options.ctrlKey ?? false,
      shiftKey: options.shiftKey ?? false,
      metaKey: options.metaKey ?? false,
      altKey: options.altKey ?? false,
    });

    (this.stage as any).setPointersPositions({ clientX: startStagePos.x, clientY: startStagePos.y });
    shape.fire('mousedown', { evt: startEvt, target: shape, currentTarget: shape });
    shape.fire('dragstart', { evt: startEvt, target: shape, currentTarget: shape });
    await this.wait(10);

    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const nextX = startX + (endX - startX) * t;
      const nextY = startY + (endY - startY) * t;
      shape.position({ x: nextX, y: nextY });

      const mx = startStagePos.x + (endStagePos.x - startStagePos.x) * t;
      const my = startStagePos.y + (endStagePos.y - startStagePos.y) * t;
      (this.stage as any).setPointersPositions({ clientX: mx, clientY: my });

      const moveEvt = new MouseEvent('mousemove', {
        clientX: mx,
        clientY: my,
        bubbles: true,
        cancelable: true,
        view: window,
        button: 0,
      });
      shape.fire('dragmove', { evt: moveEvt, target: shape, currentTarget: shape });

      if (sampleGuides) {
        const counts = this.getGuideCounts();
        if (counts.alignment > maxAlignment) maxAlignment = counts.alignment;
        if (counts.distribution > maxDistribution) maxDistribution = counts.distribution;
      }

      this.stage.batchDraw();
      await this.wait(5);
    }

    const endEvt = new MouseEvent('mouseup', {
      clientX: endStagePos.x,
      clientY: endStagePos.y,
      bubbles: true,
      cancelable: true,
      view: window,
      button: 0,
    });
    (this.stage as any).setPointersPositions({ clientX: endStagePos.x, clientY: endStagePos.y });
    shape.fire('dragend', { evt: endEvt, target: shape, currentTarget: shape });
    shape.fire('mouseup', { evt: endEvt, target: shape, currentTarget: shape });
    await this.wait(50);

    return { maxAlignment, maxDistribution };
  }
  
  async resizeShape(id: string, handle: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top' | 'bottom' | 'left' | 'right', deltaX: number, deltaY: number): Promise<void> {
    await this.clickShape(id);
    await this.wait(100);
    const transformer = this.stage.findOne('Transformer');
    if (!transformer) throw new Error('Transformer not found');
    const handlePos = this.getTransformHandlePosition(id, handle);
    const endPos = { x: handlePos.x + deltaX, y: handlePos.y + deltaY };
    const startStagePos = this.canvasToClient(handlePos);
    const endStagePos = this.canvasToClient(endPos);
    await this.simulateMouseEvent('mousedown', startStagePos);
    await this.simulateDrag(startStagePos, endStagePos);
    await this.simulateMouseEvent('mouseup', endStagePos);
  }
  
  async rectangleSelect(x1: number, y1: number, x2: number, y2: number): Promise<void> {
    // Use TestHarness for programmatic selection (most reliable)
    const harness = (window as any).TestHarness;
    if (harness?.selectMany) {
      // Find all shapes within the rectangle bounds
      const allShapes = this.getAllShapes();
      const withinBounds = allShapes.filter(s => {
        const sx = s.x;
        const sy = s.y;
        const ex = s.x + s.width;
        const ey = s.y + s.height;
        // Shape overlaps with selection rectangle
        return !(ex < x1 || sx > x2 || ey < y1 || sy > y2);
      });
      const ids = withinBounds.map(s => s.id);
      if (ids.length > 0) {
        harness.selectMany(ids);
      }
      await this.wait(20);
      return;
    }
    
    // Fallback: DOM-based rectangle selection
    const startPos = this.canvasToClient({ x: x1 - 30, y: y1 - 30 });
    const endPos = this.canvasToClient({ x: x2 + 30, y: y2 + 30 });
    await this.simulateMouseEvent('mousemove', startPos);
    await this.simulateMouseEvent('mousedown', startPos);
    await this.simulateDrag(startPos, endPos);
    await this.simulateMouseEvent('mouseup', endPos);
  }
  
  async panCanvas(deltaX: number, deltaY: number): Promise<void> {
    const centerCanvas = { x: this.stage.width() / 2, y: this.stage.height() / 2 };
    const startPos = this.canvasToClient(centerCanvas);
    const endPos = this.canvasToClient({ x: centerCanvas.x + deltaX, y: centerCanvas.y + deltaY });
    const originalDraggable = this.stage.draggable();
    try {
      this.stage.draggable(true);
      await this.simulateMouseEvent('mousemove', startPos);
      await this.simulateMouseEvent('mousedown', startPos);
      await this.simulateDrag(startPos, endPos);
      await this.simulateMouseEvent('mouseup', endPos);
    } finally {
      this.stage.draggable(originalDraggable);
    }
  }
  
  async zoomCanvas(zoomFactor: number, centerX?: number, centerY?: number): Promise<void> {
    const centerCanvas = { x: centerX ?? this.stage.width() / 2, y: centerY ?? this.stage.height() / 2 };
    const clientCenter = this.canvasToClient(centerCanvas);
    await this.simulateMouseEvent('mousemove', clientCenter);
    const before = this.stage.scaleX();
    const deltaY = zoomFactor > 1 ? -300 : 300;
    await this.simulateWheelEvent(clientCenter, deltaY);
    await this.wait(50);
    const after = this.stage.scaleX();
    if (after === before) {
      // Fallback: adjust scale for test determinism. Prefer the app's viewport
      // commit path — React owns the settled transform, so a raw Konva scale
      // write is clobbered by the next render.
      const next = Math.max(0.1, before * (zoomFactor > 1 ? 1.2 : 0.8));
      if (this.viewportCommitter) {
        this.viewportCommitter({ scale: next, offsetX: this.stage.x(), offsetY: this.stage.y() });
      } else {
        this.stage.scale({ x: next, y: next });
        this.stage.batchDraw();
      }
    }
  }

  setViewportCommitter(committer: ((next: { scale: number; offsetX: number; offsetY: number }) => void) | null): void {
    this.viewportCommitter = committer;
  }

  async dragPointer(from: { x: number; y: number }, to: { x: number; y: number }, steps = 12, options: TestMouseOptions = {}): Promise<void> {
    await this.simulateMouseEvent('mousemove', from, options);
    await this.simulateMouseEvent('mousedown', from, options);
    await this.simulateDrag(from, to, steps);
    await this.simulateMouseEvent('mouseup', to, options);
  }
  
  private canvasToClient(pos: { x: number; y: number }): { x: number; y: number } {
    const rect = this.stage.container().getBoundingClientRect();
    const scale = this.stage.scaleX();
    const stagePos = this.stage.position();
    return { x: rect.left + stagePos.x + pos.x * scale, y: rect.top + stagePos.y + pos.y * scale };
  }
  
  private getTransformHandlePosition(shapeId: string, handle: string): { x: number; y: number } {
    const shape = this.stage.findOne(`#${shapeId}`);
    if (!shape) throw new Error(`Shape ${shapeId} not found`);
    // relativeTo keeps the rect in canvas space; canvasToClient applies the
    // stage transform exactly once (getClientRect() alone is already stage-space).
    const box = shape.getClientRect({ relativeTo: this.stage });
    switch (handle) {
      case 'top-left': return { x: box.x, y: box.y };
      case 'top-right': return { x: box.x + box.width, y: box.y };
      case 'bottom-left': return { x: box.x, y: box.y + box.height };
      case 'bottom-right': return { x: box.x + box.width, y: box.y + box.height };
      case 'top': return { x: box.x + box.width / 2, y: box.y };
      case 'bottom': return { x: box.x + box.width / 2, y: box.y + box.height };
      case 'left': return { x: box.x, y: box.y + box.height / 2 };
      case 'right': return { x: box.x + box.width, y: box.y + box.height / 2 };
      default: throw new Error(`Unknown handle: ${handle}`);
    }
  }
  
  private async simulateMouseEvent(type: string, pos: { x: number; y: number }, options: TestMouseOptions = {}): Promise<void> {
    (this.stage as any).setPointersPositions({ clientX: pos.x, clientY: pos.y });
    const event = new MouseEvent(type, {
      clientX: pos.x,
      clientY: pos.y,
      bubbles: true,
      cancelable: true,
      view: window,
      ctrlKey: options.ctrlKey ?? false,
      shiftKey: options.shiftKey ?? false,
      metaKey: options.metaKey ?? false,
      altKey: options.altKey ?? false,
      button: options.button ?? 0
    } as MouseEventInit);
    this.stage.getContent().dispatchEvent(event);
    await this.wait(10);
  }
  
  private async simulateDrag(from: { x: number; y: number }, to: { x: number; y: number }, steps = 10): Promise<void> {
    for (let i = 0; i <= steps; i++) {
      const progress = i / steps;
      const x = from.x + (to.x - from.x) * progress;
      const y = from.y + (to.y - from.y) * progress;
      await this.simulateMouseEvent('mousemove', { x, y });
      await this.wait(5);
    }
  }
  
  private async simulateWheelEvent(pos: { x: number; y: number }, deltaY: number): Promise<void> {
    (this.stage as any).setPointersPositions({ clientX: pos.x, clientY: pos.y });
    const event = new WheelEvent('wheel', { clientX: pos.x, clientY: pos.y, deltaY, bubbles: true, cancelable: true, view: window } as WheelEventInit);
    this.stage.getContent().dispatchEvent(event);
    await this.wait(10);
  }
  
  // Key simulation reserved for future keyboard interaction tests
  
  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  dumpCanvasState(): void {
    console.log('Canvas State:', { shapes: this.getAllShapes(), selected: this.getSelectedShapes(), stage: this.getStagePosition() });
  }
  
  async takeDebugScreenshot(): Promise<string> {
    const dataURL = this.stage.toDataURL();
    console.log('Screenshot taken:', dataURL.substring(0, 50) + '...');
    return dataURL;
  }
}
